import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/catch';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types';
import {
	getNextgenSettingsIssueTypesUrl,
	getNextgenSettingsJSDAutomationUrl,
} from '@atlassian/jira-project-settings-apps-common/src/urls';
import { navigate as navigateAction } from '../actions';
import { getPushStateNavigator } from './utils';

export const projectSettingsPageUrl = (projectKey: string, relativeUrl: string) =>
	`/jira/servicedesk/projects/${projectKey}/settings/${relativeUrl}`;

/**
 * Navigate to a given location.
 *
 * @param projectKey is the projectKey of the current project
 * @param fragment URL relative to the project settings base URL.
 *
 * Eventually there will be no PushStateNavigator provided by the SD monolith
 * and action-based navigation will be the norm. Start off on the right foot
 * by not assuming that this will be side-effectful; expect an action to be
 * returned every time we navigate.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const navigate = (projectKey: string, fragment: string): Observable<any> =>
	Observable.fromPromise(getPushStateNavigator())
		.mergeMap((pushStateNavigator) => {
			pushStateNavigator.navigateToFragment(fragment);
			return Observable.empty<never>();
		})
		.catch(() => Observable.of(navigateAction(projectSettingsPageUrl(projectKey, fragment))));

const getNextgenAutomationUrl = (projectKey: string) =>
	getNextgenSettingsJSDAutomationUrl(projectKey);

export const getAutomationUrl = (baseUrl: string, projectKey: string): string =>
	getNextgenAutomationUrl(projectKey);

export const getProjectUrl = (baseUrl: string, projectKey: string): string =>
	`/projects/${projectKey}`;

const getNextgenIssueTypesUrl = (projectKey: string, issueTypeId?: string) =>
	getNextgenSettingsIssueTypesUrl(SERVICE_DESK_PROJECT, projectKey, issueTypeId);

export const getRequestTypesUrl = (
	baseUrl: string,
	projectKey: string,
	isSimplified = false,
): string =>
	isSimplified
		? getNextgenIssueTypesUrl(projectKey)
		: `/jira/servicedesk/projects/${projectKey}/settings/request-types`;

export const getChangeRequestTypesUrl = (
	baseUrl: string,
	projectKey: string,
	isSimplified = false,
): string =>
	isSimplified
		? getNextgenIssueTypesUrl(projectKey)
		: `${getRequestTypesUrl(baseUrl, projectKey, isSimplified)}/category/changes`;

export const getProjectSettingsUrl = (
	baseUrl: string,
	projectKey: string,
	isSimplified = false,
): string => {
	const classicSettingsUrl = `/plugins/servlet/project-config/${projectKey}`;
	return isSimplified ? getNextgenIssueTypesUrl(projectKey) : classicSettingsUrl;
};

export const getWorkflowSettingsUrl = (
	baseUrl: string,
	projectKey: string,
	issueTypeId: string | null,
	isSimplified = false,
): string => {
	if (!issueTypeId) {
		return isSimplified
			? getNextgenIssueTypesUrl(projectKey)
			: `/plugins/servlet/project-config/${projectKey}/workflows`;
	}

	return isSimplified
		? getNextgenIssueTypesUrl(projectKey, issueTypeId)
		: `/plugins/servlet/project-config/${projectKey}/issuetypes/${issueTypeId}/workflow`;
};

export const getJsmChangeEditionUrl = (baseUrl: string): string =>
	`${baseUrl}/admin/billing/applications/change-edition/jira-servicedesk.ondemand`;

export const getChangeManagementSettingsUrl = (baseUrl: string, projectKey: string): string =>
	`${baseUrl}/jira/servicedesk/projects/${projectKey}/settings/change-management`;

export const getUserManagementUrl = (): string => '/admin/users?referrerName=JIRA';
